import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { useApp } from '@/contexts/AppContext';
import { generateReportPDF, downloadCSV } from '@/utils/pdfUtils';
import { useInventory } from '@/contexts/InventoryContext';

const COLORS = ['#1a2b4a', '#c9a84c', '#22c55e', '#ef4444', '#f59e0b', '#6b7280', '#8b5cf6', '#ec4899'];

export default function InventoryReports() {
  const { addToast } = useApp();
  const { items, transactions, vendors, purchaseOrders, goodsReceivedNotes, getAllPendingItems } = useInventory();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fromDate = dateFrom ? new Date(dateFrom) : new Date('2000-01-01');
  const toDate = dateTo ? new Date(dateTo) : new Date('2100-01-01');

  const txnsInRange = useMemo(() => {
    return transactions.filter((t) => {
      const d = new Date(t.created_at);
      return d >= fromDate && d <= toDate;
    });
  }, [transactions, fromDate, toDate]);

  // Stock Report data
  const stockByCategory = useMemo(() => {
    const cats = [...new Set(items.map((i) => i.category))];
    return cats.map((cat) => {
      const catItems = items.filter((i) => i.category === cat);
      const totalQty = catItems.reduce((s, i) => s + i.current_qty, 0);
      const lowStock = catItems.filter((i) => i.current_qty < i.min_stock_level).length;
      return { name: cat, items: catItems.length, totalQty, lowStock };
    });
  }, [items]);

  const lowStockItems = useMemo(() => {
    return items.filter((i) => i.current_qty < i.min_stock_level);
  }, [items]);

  // Consumption by type
  const consumptionByType = useMemo(() => {
    const purchases = txnsInRange.filter((t) => t.type === 'purchase').reduce((s, t) => s + (t.qty || 0), 0);
    const usage = txnsInRange.filter((t) => t.type === 'usage').reduce((s, t) => s + (t.qty || 0), 0);
    const adjustments = txnsInRange.filter((t) => t.type === 'adjustment').reduce((s, t) => s + Math.abs(t.qty || 0), 0);
    return [
      { name: 'Purchase', value: purchases },
      { name: 'Usage', value: usage },
      { name: 'Adjustment', value: adjustments },
    ];
  }, [txnsInRange]);

  // Purchase by vendor
  const purchaseByVendor = useMemo(() => {
    const map = new Map<string, number>();
    txnsInRange.filter((t) => t.type === 'purchase' && t.vendor_id).forEach((t) => {
      const v = vendors.find((vnd) => vnd.id === t.vendor_id);
      const name = v?.name || t.vendor_id || 'Unknown';
      map.set(name, (map.get(name) || 0) + (t.price || 0));
    });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [txnsInRange, vendors]);

  const handleGeneratePDF = (title: string, getRows: () => Record<string, string | number>[], getCols: () => { header: string; dataKey: string }[]) => {
    const rows = getRows();
    const cols = getCols();
    if (rows.length === 0) {
      addToast('error', 'No data available for this report');
      return;
    }
    generateReportPDF(title, cols, rows, dateFrom && dateTo ? `Period: ${dateFrom} to ${dateTo}` : undefined);
    addToast('success', `${title} PDF downloaded`);
  };

  const handleDownloadCSV = (title: string, getRows: () => Record<string, string | number>[], getCols: () => { header: string; key: string }[]) => {
    const rows = getRows();
    const cols = getCols();
    if (rows.length === 0) {
      addToast('error', 'No data available for this report');
      return;
    }
    downloadCSV(title.replace(/\s+/g, '-'), cols, rows);
    addToast('success', `${title} CSV downloaded`);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Inventory Reports</h1>
        <p className="text-navy-500 mt-1">Stock, consumption, and purchase analytics.</p>
      </div>

      {/* Date Filter */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>
        </div>
      </div>

      {/* Stock Report */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Stock Levels by Category</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Stock Report', () => stockByCategory.map((c) => ({ category: c.name, items: c.items, totalQty: c.totalQty, lowStock: c.lowStock })), () => [{ header: 'Category', dataKey: 'category' }, { header: 'Items', dataKey: 'items' }, { header: 'Total Qty', dataKey: 'totalQty' }, { header: 'Low Stock', dataKey: 'lowStock' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Stock Report', () => stockByCategory.map((c) => ({ category: c.name, items: c.items, totalQty: c.totalQty, lowStock: c.lowStock })), () => [{ header: 'Category', key: 'category' }, { header: 'Items', key: 'items' }, { header: 'Total Qty', key: 'totalQty' }, { header: 'Low Stock', key: 'lowStock' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stockByCategory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="totalQty" fill="#1a2b4a" radius={[4, 4, 0, 0]} maxBarSize={30} />
                <Bar dataKey="lowStock" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={30} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div>
            <h4 className="text-sm font-medium text-navy-700 mb-3">Low Stock Items ({lowStockItems.length})</h4>
            <div className="space-y-2 max-h-48 overflow-auto">
              {lowStockItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-2 bg-red-50 border border-red-100 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-navy-800">{item.name}</div>
                    <div className="text-xs text-navy-500">{item.category}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-red-600">{item.current_qty} / {item.min_stock_level}</div>
                    <div className="text-xs text-red-400">Below minimum</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-4 mt-2 text-xs">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-navy-800" /> Total Qty</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> Low Stock</span>
        </div>
      </div>

      {/* Consumption Report */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Consumption Breakdown</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Consumption Report', () => consumptionByType.map((c) => ({ type: c.name, quantity: c.value })), () => [{ header: 'Type', dataKey: 'type' }, { header: 'Quantity', dataKey: 'quantity' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Consumption Report', () => consumptionByType.map((c) => ({ type: c.name, quantity: c.value })), () => [{ header: 'Type', key: 'type' }, { header: 'Quantity', key: 'quantity' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={consumptionByType} cx="50%" cy="50%" outerRadius={70} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {consumptionByType.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Purchase by Vendor */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Purchase by Vendor</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Purchase Report', () => purchaseByVendor.map((v) => ({ vendor: v.name, amount: v.value })), () => [{ header: 'Vendor', dataKey: 'vendor' }, { header: 'Amount (Rs.)', dataKey: 'amount' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Purchase Report', () => purchaseByVendor.map((v) => ({ vendor: v.name, amount: v.value })), () => [{ header: 'Vendor', key: 'vendor' }, { header: 'Amount (Rs.)', key: 'amount' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={purchaseByVendor} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={140} />
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} formatter={(v: number) => [`Rs. ${v.toLocaleString('en-IN')}`, 'Amount']} />
                <Bar dataKey="value" fill="#c9a84c" radius={[0, 4, 4, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Purchase Order Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Purchase Order Status</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {(() => {
              const byStatus = {
                ordered: purchaseOrders.filter((po) => po.status === 'ordered').length,
                partial: purchaseOrders.filter((po) => po.status === 'partial').length,
                received: purchaseOrders.filter((po) => po.status === 'received').length,
                cancelled: purchaseOrders.filter((po) => po.status === 'cancelled').length,
              };
              return Object.entries(byStatus).map(([status, count]) => (
                <div key={status} className="p-4 rounded-lg border border-navy-100 bg-navy-50/50">
                  <div className="text-2xl font-bold text-navy-800">{count}</div>
                  <div className="text-xs text-navy-500 capitalize mt-1">
                    {status === 'ordered' && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Ordered</span>}
                    {status === 'partial' && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Partial</span>}
                    {status === 'received' && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Received</span>}
                    {status === 'cancelled' && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Cancelled</span>}
                  </div>
                </div>
              ));
            })()}
          </div>
          <div className="mt-4 pt-3 border-t border-navy-100 flex justify-between text-xs">
            <span className="text-navy-500">Total POs: <strong className="text-navy-800">{purchaseOrders.length}</strong></span>
            <span className="text-navy-500">GRNs: <strong className="text-navy-800">{goodsReceivedNotes.length}</strong></span>
            <span className="text-navy-500">Total Value: <strong className="text-navy-800">Rs. {purchaseOrders.reduce((s, po) => s + po.total_amount, 0).toLocaleString('en-IN')}</strong></span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Vendor Pending Summary</h3>
          </div>
          <div className="max-h-56 overflow-auto">
            {(() => {
              const pendingItems = getAllPendingItems();
              const byVendor = new Map<string, { name: string; pending: number; itemCount: number }>();
              pendingItems.forEach((p) => {
                if (!byVendor.has(p.vendorId)) byVendor.set(p.vendorId, { name: p.vendorName, pending: 0, itemCount: 0 });
                const entry = byVendor.get(p.vendorId)!;
                entry.pending += p.qtyPending;
                entry.itemCount += 1;
              });
              const vendorList = Array.from(byVendor.entries());
              if (vendorList.length === 0) return <div className="text-xs text-green-600 text-center py-6"><i className="ri-check-double-line" /> All vendors delivered — nothing pending!</div>;
              return vendorList.map(([vId, data]) => (
                <div key={vId} className="flex items-center justify-between p-3 border-b border-navy-50 last:border-b-0">
                  <div>
                    <div className="text-sm font-medium text-navy-800">{data.name}</div>
                    <div className="text-xs text-navy-500">{data.itemCount} item{data.itemCount > 1 ? 's' : ''} pending</div>
                  </div>
                  <span className="text-sm font-bold text-red-600">{data.pending} units</span>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* Vendor Report */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Vendor Summary</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Vendor Report', () => vendors.map((v) => {
              const totalPurchased = transactions.filter((t) => t.vendor_id === v.id && t.type === 'purchase').reduce((s, t) => s + (t.price || 0), 0);
              return { vendor: v.name, gstin: v.gstin, totalPurchased, terms: v.payment_terms };
            }), () => [{ header: 'Vendor', dataKey: 'vendor' }, { header: 'GSTIN', dataKey: 'gstin' }, { header: 'Total Purchased', dataKey: 'totalPurchased' }, { header: 'Terms', dataKey: 'terms' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Vendor Report', () => vendors.map((v) => {
              const totalPurchased = transactions.filter((t) => t.vendor_id === v.id && t.type === 'purchase').reduce((s, t) => s + (t.price || 0), 0);
              return { vendor: v.name, gstin: v.gstin, totalPurchased, terms: v.payment_terms };
            }), () => [{ header: 'Vendor', key: 'vendor' }, { header: 'GSTIN', key: 'gstin' }, { header: 'Total Purchased', key: 'totalPurchased' }, { header: 'Terms', key: 'terms' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Vendor</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">GSTIN</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Payment Terms</th>
                <th className="text-right px-4 py-2 font-semibold text-navy-700">Total Purchased</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => {
                const totalPurchased = transactions.filter((t) => t.vendor_id === v.id && t.type === 'purchase').reduce((s, t) => s + (t.price || 0), 0);
                return (
                  <tr key={v.id} className="border-t border-navy-100 hover:bg-navy-50/50">
                    <td className="px-4 py-2.5 text-navy-700 text-xs font-medium">{v.name}</td>
                    <td className="px-4 py-2.5 text-navy-500 text-xs">{v.gstin}</td>
                    <td className="px-4 py-2.5 text-navy-600 text-xs">{v.payment_terms}</td>
                    <td className="px-4 py-2.5 text-navy-700 text-xs text-right font-medium">Rs. {totalPurchased.toLocaleString('en-IN')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{items.length}</div>
          <div className="text-xs text-navy-500 mt-1">Total Items</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{lowStockItems.length}</div>
          <div className="text-xs text-navy-500 mt-1">Low Stock</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{txnsInRange.length}</div>
          <div className="text-xs text-navy-500 mt-1">Transactions</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{vendors.length}</div>
          <div className="text-xs text-navy-500 mt-1">Vendors</div>
        </div>
      </div>
    </div>
  );
}